import {
  IconArrowInbox,
  IconPlus,
  Button,
  Flex,
  ActiveFilter,
  SequenceMenuItemType
} from '@sequencehq/core-components'
import Page from 'components/Page'
import { StickyPanelFrame } from 'components/Page/StickyPanelFrame'
import { Dispatch, FC, memo, SetStateAction, useState } from 'react'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import { CustomersMagicTable } from './CustomersMagicTable'
import { CustomersExportToCsv } from 'modules/Customers/components/CustomersExportToCsv'
import { CustomerImportModal } from './components/modals/customerImport/CustomerImportModal'
import {
  CustomerImportsInFlight,
  InFlightCustomerImport
} from './components/customerImportsInFlight/CustomerImportsInFlight'
import { ProtectedButton } from 'lib/rbac/ProtectedButton'
import { SavedViewEditor } from 'SavedViews/components/SavedViewEditor'
import { SavedViewTitle } from 'SavedViews/components/SavedViewTitle'
import { useSavedView } from 'SavedViews/hooks/useSavedView'

const Customers: FC<{
  title?: string
  forcedActiveFilters?: ActiveFilter[]
  kebabMenuItems?: (
    setSavingFilters: Dispatch<SetStateAction<false | ActiveFilter[]>>
  ) => SequenceMenuItemType[] | undefined
  onUpdateSavedFilters?: (
    updatedActiveFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
}> = memo(
  ({
    title = 'Customers',
    forcedActiveFilters,
    kebabMenuItems,
    onUpdateSavedFilters
  }) => {
    const [showImportModal, setShowImportModal] = useState(false)
    const [inFlightImports, setInFlightImports] = useState<
      InFlightCustomerImport[]
    >([])
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const {
      isSavedView,
      savingFilters,
      setSavingFilters,
      forceNewSavedView,
      onSaveFilters,
      onDismissSaveFilters,
      showSavedViewEditor
    } = useSavedView()

    const richTitle = showSavedViewEditor ? (
      <SavedViewEditor
        activeFilters={savingFilters || []}
        sortBy={searchParams.get('sortBy') ?? null}
        onDismiss={onDismissSaveFilters}
        forceNewSavedView={forceNewSavedView}
        entityType="CUSTOMERS"
      />
    ) : (
      <SavedViewTitle
        title={title}
        kebabMenuItems={kebabMenuItems?.(setSavingFilters)}
      />
    )

    const menuButtons = savingFilters ? null : (
      <Flex gap="8px">
        <CustomersExportToCsv />
        <ProtectedButton permission="org.customer:create">
          <Button
            variant="secondary"
            data-testid="customers.import"
            onClick={() => setShowImportModal(true)}
            leadingIcon={<IconArrowInbox color="inherit" />}
          >
            Import
          </Button>
        </ProtectedButton>
      </Flex>
    )

    return (
      <>
        <StickyPanelFrame>
          <Page
            title={title}
            richTitle={richTitle}
            addNewConfig={
              savingFilters
                ? undefined
                : {
                    button: (
                      <ProtectedButton permission="org.customer:create">
                        <Button
                          variant="primary"
                          leadingIcon={<IconPlus color="inherit" />}
                          onClick={() => navigate('/customers/create')}
                          data-testid="TopNavigation.add-new"
                        >
                          New customer
                        </Button>
                      </ProtectedButton>
                    )
                  }
            }
            menuButtons={menuButtons}
            paddingBottom={0}
          >
            <CustomersMagicTable
              onSaveFilters={onSaveFilters}
              onUpdateSavedFilters={
                isSavedView ? onUpdateSavedFilters : undefined
              }
              forcedActiveFilters={forcedActiveFilters}
              lockedControls={savingFilters && savingFilters.length > 0}
            />
            <CustomerImportsInFlight
              imports={inFlightImports}
              onDismiss={dismissedId =>
                setInFlightImports(imports =>
                  imports.filter(
                    startedImport => startedImport.id !== dismissedId
                  )
                )
              }
            />
          </Page>
          <Outlet />
        </StickyPanelFrame>
        <CustomerImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportStarted={startedImport =>
            setInFlightImports(imports => [
              ...imports,
              { ...startedImport, id: crypto.randomUUID() }
            ])
          }
        />
      </>
    )
  }
)

export default Customers
